import type { Language } from '@offer-catalog/services/i18n'
import type { Department } from '@/types/treeViewTypes'
import { VpIcon, VpLoader } from '@vtmn-play/react/headless'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import GoBackLink from '@/components/goBackLink/GoBackLink'
import { useDepartmentPic, useDepartments } from '@/hooks/catalog'
import { ANALYTICS_EVENTS, ANALYTICS_JOURNEY_CHANNELS, trackAnalyticsEvent } from '@/services/analytics'
import './departments.scss'

function DepartmentItem({
  department,
  universeId,
  currentLanguage,
  imageUrl,
  onSelect,
}: Readonly<{
  department: Department
  universeId: number
  currentLanguage: Language
  imageUrl: string
  onSelect: (department: Department, universeId: number) => void
}>) {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, selectedDepartment: Department, actualUniverseId: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect(selectedDepartment, actualUniverseId)
    }
  }

  return (
    <button
      data-testid={`department-block_${department.id}`}
      type="button"
      className="department"
      onClick={() => onSelect(department, universeId)}
      onKeyDown={e => handleKeyDown(e, department, universeId)}
    >
      <div className="department-picture-wrapper">
        {imageUrl
          ? (
              <img src={`${imageUrl}?format=auto&f=969x0`} alt="" />
            )
          : <VpIcon name="image" size={48} />}
      </div>
      <span className="department-label">
        {department.labels[currentLanguage].replace(/(\/)/g, ' $1 ')}
      </span>
    </button>
  )
}

export function Departments() {
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const currentLanguage = i18n.language.split('-')[0] as Language
  const params = useParams()
  const universeId = params.universeId
  const season = params.season as string

  const { data: fullUniverse, isLoading } = useDepartments(season, Number(universeId))
  const { data: imageUrls } = useDepartmentPic(fullUniverse?.departments.map(department => department.id) ?? [])

  const selectDepartment = async (department: Department, actualUniverseId: number) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.NAV_DEPARTMENT_SELECTED, {
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.HIERARCHY_NAVIGATION,
      season,
      universe_id: actualUniverseId,
      universe_label: fullUniverse?.labels[currentLanguage] || `ID ${fullUniverse?.id.toString()}`,
      department_id: department.id,
      department_label: department.labels[currentLanguage],
    })

    await navigate(`/${season}/universe/${actualUniverseId}/department/${department.id}`)
  }

  return isLoading
    || !fullUniverse
    || fullUniverse?.departments?.length === 1
    ? (
        <div className="loader-container global">
          <VpLoader />
        </div>
      )
    : (
        <div data-testid="departments-container" className="departments-container">
          <div className="departments-title" data-testid="departments-title">
            <GoBackLink />
            <h1 className="departments-title-universe">
              <span>{fullUniverse.labels[currentLanguage] || fullUniverse.id.toString()}</span>
              <div className="departments-count-subtitle">
                {fullUniverse.departments.length}
                {' '}
                {t('departments')}
              </div>
            </h1>
          </div>
          <div data-testid="departments" className="departments">
            {fullUniverse.departments.map(department => (
              <DepartmentItem
                key={department.id}
                department={department}
                imageUrl={imageUrls?.[department.id] ?? ''}
                universeId={fullUniverse.id}
                currentLanguage={currentLanguage}
                onSelect={selectDepartment}
              />
            ))}
          </div>
        </div>
      )
}
